import type { ApiKey, CreateApiKeyResponse } from '@ytclip/shared'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/field.tsx'
import { CopyButton } from '@/components/ui/misc.tsx'
import { useCreateApiKey, useUpdateApiKey } from './hooks.ts'

interface LimitFormState {
  name: string
  limit: string
}

function parseLimit(value: string): number | null {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function CreateKeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [form, setForm] = useState<LimitFormState>({ name: '', limit: '' })
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null)
  const createKey = useCreateApiKey()

  function close(next: boolean) {
    onOpenChange(next)

    if (!next) {
      setForm({ name: '', limit: '' })
      setCreated(null)
    }
  }

  async function submit() {
    if (!form.name.trim()) return

    setCreated(
      await createKey.mutateAsync({
        name: form.name.trim(),
        maxBudgetUsd: parseLimit(form.limit),
      }),
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={created ? 'API key dibuat' : 'Buat API key'}
      description={
        created
          ? 'Salin sekarang — kunci ini tidak bisa dilihat lagi setelah dialog ditutup.'
          : 'Beri nama supaya mudah dikenali, dan batasi pemakaiannya bila perlu.'
      }
    >
      {created ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-md bg-warning-container p-4 text-on-warning-container">
            <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <p className="text-body-md">
              Kunci ini hanya ditampilkan satu kali. Simpan di tempat yang aman sekarang.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-md bg-surface-container-low p-4">
            <code className="min-w-0 flex-1 break-all font-mono text-label-md text-on-surface">
              {created.secret}
            </code>
            <CopyButton value={created.secret} />
          </div>

          <Button className="w-full" onClick={() => close(false)}>
            Sudah saya simpan
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <TextField
            label="Nama"
            placeholder="Aplikasi produksi"
            maxLength={64}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <TextField
            label="Batas pemakaian (opsional)"
            prefix="$"
            inputMode="decimal"
            placeholder="Tanpa batas"
            value={form.limit}
            onChange={(event) => setForm({ ...form, limit: event.target.value })}
            hint="Kosongkan untuk mengikuti saldo akun."
          />
          <Button
            className="w-full"
            size="lg"
            disabled={!form.name.trim()}
            loading={createKey.isPending}
            onClick={() => void submit()}
          >
            Buat key
          </Button>
        </div>
      )}
    </Dialog>
  )
}

export function EditKeyDialog({
  apiKey,
  onOpenChange,
}: {
  apiKey: ApiKey | null
  onOpenChange: (open: boolean) => void
}) {
  const [form, setForm] = useState<LimitFormState>({ name: '', limit: '' })
  const updateKey = useUpdateApiKey()

  useEffect(() => {
    if (apiKey) {
      setForm({ name: apiKey.name, limit: apiKey.maxBudgetUsd?.toString() ?? '' })
    }
  }, [apiKey])

  async function submit() {
    if (!apiKey) return

    await updateKey.mutateAsync({
      token: apiKey.token,
      body: { name: form.name.trim(), maxBudgetUsd: parseLimit(form.limit) },
    })

    onOpenChange(false)
  }

  return (
    <Dialog
      open={Boolean(apiKey)}
      onOpenChange={onOpenChange}
      title="Ubah API key"
      description="Nama dan batas pemakaian bisa diubah kapan saja."
    >
      <div className="space-y-5">
        <TextField
          label="Nama"
          maxLength={64}
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <TextField
          label="Batas pemakaian"
          prefix="$"
          inputMode="decimal"
          placeholder="Tanpa batas"
          value={form.limit}
          onChange={(event) => setForm({ ...form, limit: event.target.value })}
          hint="Kosongkan untuk menghapus batas."
        />
        <Button
          className="w-full"
          size="lg"
          disabled={!form.name.trim()}
          loading={updateKey.isPending}
          onClick={() => void submit()}
        >
          Simpan perubahan
        </Button>
      </div>
    </Dialog>
  )
}
